import { Dispatch, useState } from 'react';

import clsx from 'clsx';

import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  MinusCircleIcon,
  PencilIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/20/solid';
import * as Papa from 'papaparse';
import date from 'date-and-time';

import { Card } from '../base/Card';
import { LineGraph } from './LineGraph';
import { Button } from '../base/Button';
import { TextField } from '../base/TextField';
import { GUIDtoHEX } from '../../utils/GUIDtoHEX';
import { LoadingWheel } from '../base/LoadingWheel';
import { StatisticStateReducerActions } from './Statistics';
import { IStatistic } from '../../types/Statistic/Statistic';
import { IDataPoint, ISimpleDataPoint } from '../../types/DataPoint/DataPoint';
import { useGraphSettings } from '../../contexts/GraphSettingsContext';
import {
  accumulatedDataPoints,
  averagedDataPoints,
  graphableDataPoints,
} from '../../constants/datapointLists';
import useDataPointsByHardwareModel from '../../data/datapoint/useDataPointsByHardwareModel';
import { IAppliance } from '../../types/Appliance/Appliance';
import { IGraphDataResponse } from '../../data/datapoint/useGraphData';
import { useCSVExporter } from '../../hooks/useDataExporter';

interface DatapointChipProps extends IDataPoint {
  selected: boolean;
  onClick: (id: string) => void;
}

function DatapointChip({ id, name, selected, onClick }: DatapointChipProps) {
  return (
    <div
      key={id}
      onClick={() => onClick(id)}
      className={clsx(
        'flex items-center justify-between py-2 px-3 rounded shadow-md bg-gray-100 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-900 hover:cursor-pointer',
        selected ? 'bg-gray-300 dark:bg-gray-900' : ''
      )}
    >
      <div className="flex items-center space-x-3">
        <div
          className="w-6 h-6 rounded-lg"
          style={{ backgroundColor: GUIDtoHEX(id) }}
        />
        <p>{name}</p>
      </div>
      <span>
        {selected ? (
          <MinusCircleIcon className="w-5 h-5" />
        ) : (
          <PlusCircleIcon className="w-5 h-5" />
        )}
      </span>
    </div>
  );
}

interface GraphCardProps extends Omit<IStatistic, 'id'> {
  id: string;
  appliance: IAppliance;
  dispatch: Dispatch<StatisticStateReducerActions>;
}

export function GraphCard({
  id,
  title,
  dataPointIds,
  appliance,
  dispatch,
}: GraphCardProps) {
  const {
    currentValue: { granularity },
  } = useGraphSettings();
  const { dataPoints } = useDataPointsByHardwareModel(
    appliance.hardwareModelId
  );
  const { fromGraphDataToCSV, isLoading: dataPointLookupsLoading } =
    useCSVExporter(appliance.hardwareModelId);
  const [showConfig, setShowConfig] = useState(false);
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(title);
  const [dataForExport, setDataForExport] = useState<IGraphDataResponse[]>();

  const isUsingAverages = granularity !== '5 Seconds';

  const handleSaveTitle = () => {
    dispatch({
      type: 'updateTitle',
      payload: {
        id,
        title: titleValue,
      },
    });

    setTitleEditing(false);
  };

  const handleRemoveGraph = () =>
    dispatch({ type: 'removeGraph', payload: { id } });

  const handleDatapointAdd = (dataPointId: string) => {
    dispatch({
      type: 'addDataPoint',
      payload: {
        id,
        dataPoint: dataPointId,
      },
    });
  };

  const handleDatapointRemove = (dataPointId: string) => {
    dispatch({
      type: 'removeDataPoint',
      payload: {
        id,
        dataPoint: dataPointId,
      },
    });
  };

  const handleMissingDataPoints = (dataPointIds: string[]) => {
    dataPointIds.forEach((dataPointId) => {
      handleDatapointRemove(dataPointId);
    });
  };

  const handleExportData = () => {
    if (dataForExport) {
      const csvData = fromGraphDataToCSV(dataForExport);
      const csv = Papa.unparse(csvData);

      // Add UTF-8 BOM to ensure proper encoding of special characters like °
      const blob = new Blob(['\ufeff' + csv], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `${appliance.name.trim().replace(/\s+/g, '_')}_${date.format(new Date(), 'DD-MM-YY_HH-mm-ss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          {titleEditing ? (
            <TextField
              value={titleValue}
              onChange={(e) => setTitleValue(e.currentTarget.value)}
            />
          ) : (
            <p className="text-3xl">{title}</p>
          )}
          {showConfig ? (
            titleEditing ? (
              <Button className="h-[42px]" onClick={handleSaveTitle}>
                <CheckIcon className="w-5 h-5" />
              </Button>
            ) : (
              <PencilIcon
                className="w-5 h-5"
                onClick={() => setTitleEditing(true)}
              />
            )
          ) : (
            <></>
          )}
        </div>
        {showConfig ? (
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <ChevronUpIcon
                className="w-6 h-6"
                onClick={() =>
                  dispatch({
                    type: 'moveTile',
                    payload: { id, direction: 'up' },
                  })
                }
              />
              <ChevronDownIcon
                className="w-6 h-6"
                onClick={() =>
                  dispatch({
                    type: 'moveTile',
                    payload: { id, direction: 'down' },
                  })
                }
              />
            </div>
            <TrashIcon
              className="w-6 h-6 text-red-400 dark:text-red-800"
              onClick={handleRemoveGraph}
            />
            <CheckIcon
              className="w-8 h-8"
              onClick={() => setShowConfig(false)}
            />
          </div>
        ) : (
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleExportData}
              disabled={
                dataPointLookupsLoading ||
                !dataForExport ||
                dataForExport.length === 0
              }
            >
              Export Data
            </Button>
            <Cog6ToothIcon
              className="w-8 h-8"
              onClick={() => setShowConfig(true)}
            />
          </div>
        )}
      </div>
      {showConfig && (
        <div className="mt-2 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2">
          {dataPoints.isLoading ? (
            <LoadingWheel />
          ) : (
            dataPoints
              .data!.filter((d) =>
                [...accumulatedDataPoints, ...graphableDataPoints].includes(
                  d.id
                )
              )
              .filter((d) =>
                isUsingAverages
                  ? [...accumulatedDataPoints, ...averagedDataPoints].includes(
                      d.id
                    )
                  : true
              )
              .map((d) => {
                let selected = dataPointIds.includes(d.id);
                return (
                  <DatapointChip
                    {...d}
                    key={`chip-${id}-${d.id}`}
                    selected={selected}
                    onClick={
                      selected ? handleDatapointRemove : handleDatapointAdd
                    }
                  />
                );
              })
          )}
        </div>
      )}
      <LineGraph
        identifier={id}
        dataPointIds={dataPointIds}
        onMissingDataPoints={handleMissingDataPoints}
        setDataForExport={setDataForExport}
      />
    </Card>
  );
}
